import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  useTheme,
} from '@mui/material';
import toastError from 'shared/components/snackbar/error/toastError';
import toastSuccess from 'shared/components/snackbar/success/toastSuccess';

import MDButton from 'shared/components/MDButton';
import MDBox from 'shared/components/MDBox';
import MDTypography from 'shared/components/MDTypography';

import FormField from 'shared/components/form_field';
import {
  handleValue,
  renderInput,
  noOptionsTextMinCharacters,
} from 'shared/components/autocomplete/autocompleteConfigs';
import { initialValuesBehaviors } from 'shared/pages/patients/schemas/initial_values';
import { behaviorsValidations } from 'shared/pages/patients/schemas/validations';
import FormFieldRadio from 'shared/components/form_field/form_field_radio';
import ModalPatientTemplates from 'shared/components/modals/modalPatientTemplates';
import {
  translateStatus,
  statusOptions,
  translateAplicationPhase,
  userLogged,
} from 'utils/essentialData';
import behaviorsService from 'shared/services/behaviors/behaviors.service';
import applicationPhasesService from 'shared/services/application_phases/applicationPhases.service';
import patientsService from 'shared/services/patients/patients.service';
import { multipleChips } from 'shared/components/autocomplete/autocompleteConfigs';

const radioOptionsType = [
  {
    value: 'duration',
    label: 'Duração',
  },
  {
    value: 'frequency',
    label: 'Frequência',
  },
];

function BehaviorCreate({ setStep }) {
  const theme = useTheme();
  const [initialValuesFromApi, setInitialValuesFromApi] = useState();
  const { uuid } = useParams();

  const [openModalTemplate, setOpenModalTemplate] = useState(false);
  const [selectedTemplateItem, setSelectedTemplateItem] = useState();

  const [supervisorData, setSupervisorData] = useState();
  const [therapistOptions, setTherapistOptions] = useState();
  const [applicationPhasesData, setApplicationPhasesData] = useState();

  const loggedUser = userLogged() ?? undefined;

  const filterOptionsStatus = createFilterOptions({
    stringify: (option) => translateStatus(option),
  });

  const filterOptionsApplicationPhase = createFilterOptions({
    stringify: (option) => translateAplicationPhase(option.category),
  });

  const permitionsFields = () => [99].includes(loggedUser.role);

  const handleSubmit = async (values, actions) => {
    const {
      name,
      application_phase,
      procedure,
      register_type,
      status,
      supervisor_uuid,
      therapist_uuids,
    } = values;

    try {
      const data = {
        name,
        application_phase: application_phase.uuid,
        procedure,
        register_type,
        patient_uuid: uuid,
        status,
        supervisor_uuid:
          loggedUser.role === 1 ? loggedUser.uuid : supervisor_uuid.uuid,
        therapist_uuids: therapist_uuids?.map((v) => v.uuid) ?? [],
      };

      await behaviorsService.create(data);
      toastSuccess('Arquivo criado com sucesso');
      actions.setTouched({});
      setStep('list');
    } catch (e) {
      toastError('Não foi possivel criar comportamento');
    } finally {
      actions.setSubmitting(false);
    }
  };

  const loadSupervisors = async (page = 1, limit = 25, filter = '') => {
    try {
      const filterValue = filter !== '' ? `&filter=${filter.trim()}` : '';
      const res = await patientsService.listRelationships(
        `?page=${page}&limit=${limit}&role=1&uuid=${uuid}${filterValue}`,
      );
      if (res) {
        setSupervisorData(res);
        return res.data;
      }
    } catch (e) {
      toastError(e.message);
    }
  };

  const loadApplicationPhases = async (page = 1, limit = 25, filter = '') => {
    try {
      const res = await applicationPhasesService.list(`?category=true`);

      if (res) {
        setApplicationPhasesData(res);
      }
    } catch (e) {
      toastError(e.message);
    }
  };

  const loadTherapists = async (page = 1, limit = 200, filter = '') => {
    try {
      const filterValue = filter !== '' ? `&filter=${filter.trim()}` : '';
      const res = await patientsService.listRelationships(
        `?page=${page}&limit=${limit}&role=2&uuid=${uuid}${filterValue}`,
      );
      if (res) {
        setTherapistOptions(res);
        return res.data;
      }
    } catch (e) {
      toastError(e.message);
    }
  };

  useEffect(async () => {
    if (selectedTemplateItem) {
      const vinculadosSup = await loadSupervisors();
      const vinculadosTer = await loadTherapists();

      const supervisorValido = vinculadosSup?.find(
        (s) => s.uuid === selectedTemplateItem.supervisor?.uuid,
      );

      const terapeutasValidos = (selectedTemplateItem.therapists || []).filter(
        (tTemp) => vinculadosTer?.some((tVin) => tVin.uuid === tTemp.uuid),
      );

      setInitialValuesFromApi({
        name: selectedTemplateItem.name,
        application_phase: selectedTemplateItem.application_phase,
        status: selectedTemplateItem.status,
        procedure: selectedTemplateItem.procedure,
        register_type: selectedTemplateItem.register_type,
        supervisor_uuid:
          loggedUser.role === 1 ? loggedUser.uuid : supervisorValido || null,
        therapist_uuids: terapeutasValidos,
      });
    }
  }, [selectedTemplateItem]);

  useEffect(() => {
    loadApplicationPhases();
  }, []);

  return (
    <>
      <Formik
        initialValues={initialValuesFromApi ?? initialValuesBehaviors}
        validationSchema={behaviorsValidations[0]}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          handleBlur,
        }) => {
          const {
            name: nameV,
            application_phase: application_phaseV,
            status: statusV,
            procedure: procedureV,
            register_type: register_typeV,
            supervisor_uuid: supervisor_uuidV,
            therapist_uuids: therapist_uuidsV,
          } = values;
          return (
            <Form id='user-edit-form' autoComplete='off'>
              <MDBox id='basic-info' sx={{ overflow: 'visible', pt: 3 }}>
                <MDBox display='flex' justifyContent='space-between'>
                  <MDTypography variant='h5' pb={3}>
                    Criar comportamento
                  </MDTypography>

                  <MDButton
                    variant='outlined'
                    color='primary'
                    sx={{ height: 'fit-content' }}
                    onClick={() => setOpenModalTemplate(true)}
                  >
                    Templates
                  </MDButton>
                </MDBox>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormField
                      name='name'
                      label='Comportamento *'
                      type='text'
                      value={nameV}
                      error={errors.name && touched.name}
                      success={nameV.length > 0 && !errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={applicationPhasesData ?? []}
                      getOptionLabel={(opt) =>
                        translateAplicationPhase(opt.category)
                      }
                      value={handleValue(application_phaseV)}
                      isOptionEqualToValue={(option, value) =>
                        option.uuid === value.uuid
                      }
                      onChange={(e, value) =>
                        setFieldValue('application_phase', value)
                      }
                      filterOptions={filterOptionsApplicationPhase}
                      renderOption={(props, option) => (
                        <Box component='li' {...props}>
                          {translateAplicationPhase(option.category)}
                        </Box>
                      )}
                      renderInput={(params) =>
                        renderInput(
                          'form',
                          params,
                          'application_phase',
                          'Fase de aplicação *',
                          handleBlur,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={statusOptions}
                      getOptionLabel={translateStatus}
                      value={handleValue(statusV)}
                      isOptionEqualToValue={(option, value) => option === value}
                      onChange={(e, value) => setFieldValue('status', value)}
                      filterOptions={filterOptionsStatus}
                      renderOption={(props, option) => (
                        <Box component='li' {...props}>
                          {translateStatus(option)}
                        </Box>
                      )}
                      renderInput={(params) =>
                        renderInput(
                          'form',
                          params,
                          'status',
                          'Status *',
                          handleBlur,
                        )
                      }
                    />
                  </Grid>
                </Grid>

                {permitionsFields() && (
                  <MDBox>
                    <Autocomplete
                      options={supervisorData?.data ?? []}
                      noOptionsText={noOptionsTextMinCharacters(supervisorData)}
                      getOptionLabel={(op) => op.name}
                      value={handleValue(supervisor_uuidV)}
                      isOptionEqualToValue={(option, value) =>
                        option.uuid === value.uuid
                      }
                      onChange={(e, value) => {
                        setFieldValue('supervisor_uuid', value);
                      }}
                      renderInput={(params) =>
                        renderInput(
                          'form',
                          params,
                          'supervisor_uuid',
                          'Supervisor *',
                          handleBlur,
                          (event) => {
                            const { value } = event.target;
                            if (value && value.length >= 3) {
                              loadSupervisors(1, 50, value);
                            } else {
                              setSupervisorData();
                            }
                          },
                        )
                      }
                    />
                  </MDBox>
                )}

                <MDBox>
                  <Autocomplete
                    multiple
                    options={therapistOptions?.data ?? []}
                    noOptionsText={noOptionsTextMinCharacters(therapistOptions)}
                    getOptionLabel={(option) => option?.name}
                    value={therapist_uuidsV || []}
                    isOptionEqualToValue={(option, value) =>
                      option.uuid === value.uuid
                    }
                    onChange={(e, value) =>
                      setFieldValue('therapist_uuids', value)
                    }
                    renderTags={(value, getTagProps) =>
                      multipleChips(value, getTagProps, 'name', theme)
                    }
                    renderInput={(params) =>
                      renderInput(
                        'form',
                        params,
                        'therapist_uuids',
                        'Terapeutas',
                        handleBlur,
                        (event) => {
                          const { value } = event.target;
                          if (value && value.length >= 3) {
                            loadTherapists(1, 50, value);
                          } else {
                            setTherapistOptions();
                          }
                        },
                      )
                    }
                  />
                </MDBox>

                <MDBox>
                  <FormField
                    name='procedure'
                    label='Procedimento'
                    type='text'
                    value={procedureV}
                    error={errors.procedure && touched.procedure}
                    success={procedureV.length > 0 && !errors.procedure}
                    multiline
                    rows={4}
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant='h6' pb={1}>
                    Forma de registro
                  </MDTypography>
                </MDBox>

                <MDBox>
                  <FormFieldRadio
                    name='register_type'
                    value={register_typeV}
                    options={radioOptionsType}
                    validationDirection='column'
                  />
                </MDBox>

                <MDBox display='flex' width='24rem' ml='auto' gap={3}>
                  <MDButton
                    color='secondary'
                    type='button'
                    size='large'
                    onClick={() => setStep('list')}
                    fullWidth
                    variant='outlined'
                  >
                    cancelar
                  </MDButton>
                  <MDButton
                    disabled={isSubmitting}
                    color='primary'
                    type='submit'
                    size='large'
                    fullWidth
                  >
                    SALVAR
                  </MDButton>
                </MDBox>
              </MDBox>
            </Form>
          );
        }}
      </Formik>
      <ModalPatientTemplates
        open={openModalTemplate}
        onClose={() => {
          setOpenModalTemplate(false);
        }}
        type='comportamento'
        setTemplateValue={setSelectedTemplateItem}
        requestApi={behaviorsService}
      />
    </>
  );
}

BehaviorCreate.defaultProps = {
  setStep: null,
};

BehaviorCreate.propTypes = {
  setStep: PropTypes.func,
};

export default BehaviorCreate;
